import { Text, View, Link } from "@react-pdf/renderer";
import { dateRange, fmtMonth, joinNonEmpty } from "./utils.js";
import { REFERENCE_MODE, RESUME_SECTION_TYPES } from "../editor/config.js";

// Builds a { [sectionType]: Component } map for a given style sheet.
// Each template (Classic/Modern/Minimal) supplies its own S so look & feel
// vary while data binding stays identical.
//
// Conventions for blocks:
//   - Each item uses <View wrap={false}> so multi-line entries don't break across pages.
//   - Section heading is a separate small View so the layout engine can break before it.
//   - Inline emphasis is done with nested <Text>.

export function buildPdfBlocks(S) {
  const SectionHeading = ({ children }) => (
    <View style={S.sectionHeadingWrap} wrap={false}>
      <Text style={S.sectionHeading}>{children}</Text>
      {S.sectionRule ? <View style={S.sectionRule} /> : null}
    </View>
  );

  const Bullets = ({ items }) => {
    const filtered = (items ?? []).filter(Boolean);
    if (!filtered.length) return null;
    return (
      <View style={S.highlights}>
        {filtered.map((b, i) => (
          <View key={i} style={S.bulletRow}>
            <Text style={S.bulletMark}>•</Text>
            <Text style={S.bulletText}>{b}</Text>
          </View>
        ))}
      </View>
    );
  };

  function PersonalInfoBlock({ data }) {
    if (!data) return null;
    const cityCountry = joinNonEmpty([data.city, data.state, data.country, data?.postalCode], ", ");
    const contacts = [data.email, data.phone, cityCountry].filter(Boolean);
    const SOCIAL_LABELS = {
      linkedin: "LinkedIn",
      portfolio: "Portfolio",
      github: "GitHub",
      otherLink: "Web",
    };
    const links = (data.socialLinks ?? [])
      .filter((l) => !!l?.url)
      .map((l) => ({ label: SOCIAL_LABELS[l.type] ?? l.type, v: l.url }));
    const meta = joinNonEmpty([data.nationality, data.workAuthorization]);

    return (
      <View style={S.header}>
        {data.fullName ? <Text style={S.headerName}>{data.fullName}</Text> : null}
        {data.headline ? <Text style={S.headerHeadline}>{data.headline}</Text> : null}
        {contacts.length > 0 && (
          <Text style={S.headerContact}>
            {contacts.map((c, i) => (
              <Text key={i}>
                {i > 0 ? <Text style={S.sep}>  |  </Text> : null}
                {c}
              </Text>
            ))}
          </Text>
        )}
        {links.length > 0 && (
          <Text style={S.headerContact}>
            {links.map((l, i) => (
              <Text key={l.label}>
                {i > 0 ? <Text style={S.sep}>  |  </Text> : null}
                <Link src={l.v} style={S.link}>
                  {l.label}
                </Link>
              </Text>
            ))}
          </Text>
        )}
        {meta ? <Text style={S.headerContact}>{meta}</Text> : null}
      </View>
    );
  }

  function SummaryBlock({ data }) {
    if (!data) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Summary</SectionHeading>
        <Text style={S.body}>{data}</Text>
      </View>
    );
  }

  function ExperienceBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Experience</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>
                {it.role}
                {it.role && it.company ? ", " : ""}
                <Text style={S.itemTitleAlt}>{it.company}</Text>
                {it.employmentType ? (
                  <Text style={S.itemTitleMuted}> ({it.employmentType})</Text>
                ) : null}
              </Text>
              <Text style={S.itemDate}>
                {dateRange(it.startDate, it.endDate, it.current)}
              </Text>
            </View>
            {(it.location || it.domain) && (
              <Text style={S.itemSubtitle}>
                {joinNonEmpty([it.location, it.domain])}
              </Text>
            )}
            <Bullets items={it.responsibilities} />
            {(it.techStack ?? []).length > 0 && (
              <Text style={S.chipsLine}>
                <Text style={S.labelStrong}>Tools: </Text>
                {it.techStack.join(", ")}
              </Text>
            )}
          </View>
        ))}
      </View>
    );
  }

  function EducationBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Education</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>{it.institution}</Text>
              <Text style={S.itemDate}>
                {dateRange(it.startDate, it.endDate, false)}
              </Text>
            </View>
            <Text style={S.body}>
              {joinNonEmpty([it.degree, it.specialization], ", ")}
              {it.gpa ? `  ·  GPA: ${it.gpa}` : ""}
              {it.location ? `  ·  ${it.location}` : ""}
            </Text>
            {it.normalizedLevelOfDegree ? (
              <Text style={S.meta}>
                <Text style={S.labelStrong}>Thesis: </Text>
                {it.normalizedLevelOfDegree}
              </Text>
            ) : null}
            {(it.honors ?? []).length > 0 && (
              <Text style={S.meta}>
                <Text style={S.labelStrong}>Honors: </Text>
                {it.honors.join(", ")}
              </Text>
            )}
            {(it.activities ?? []).length > 0 && (
              <Text style={S.meta}>
                <Text style={S.labelStrong}>Activities: </Text>
                {it.activities.join(", ")}
              </Text>
            )}
            {(it.coursework ?? []).length > 0 && (
              <Text style={S.meta}>
                <Text style={S.labelStrong}>Coursework: </Text>
                {it.coursework.join(", ")}
              </Text>
            )}
          </View>
        ))}
      </View>
    );
  }

  function SkillsBlock({ data }) {
    const hard = data?.technicalSkills ?? [];
    const soft = data?.softSkills ?? [];
    if (!hard.length && !soft.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Skills</SectionHeading>
        {hard.length > 0 && (
          <Text style={S.body}>
            <Text style={S.labelStrong}>Technical: </Text>
            {hard.join(", ")}
          </Text>
        )}
        {soft.length > 0 && (
          <Text style={[S.body, { marginTop: 3 }]}>
            <Text style={S.labelStrong}>Other: </Text>
            {soft.join(", ")}
          </Text>
        )}
      </View>
    );
  }

  function ProjectsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Projects</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>
                {it.name}
                {it.sourceUrl ? (
                  <Text>
                    {"  "}
                    <Link src={it.sourceUrl} style={S.linkSmall}>
                      {it.sourceUrl}
                    </Link>
                  </Text>
                ) : null}
              </Text>
              {(it.startDate || it.endDate || it.current) && (
                <Text style={S.itemDate}>
                  {dateRange(it.startDate, it.endDate, it.current)}
                </Text>
              )}
            </View>
            {it.description ? <Text style={S.body}>{it.description}</Text> : null}
            <Bullets items={it.highlights} />
          </View>
        ))}
      </View>
    );
  }

  function CertificationsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Certifications</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={[S.itemGroup, S.itemGroupTight]} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>
                {it.name}
                {it.issuer ? (
                  <Text style={S.itemTitleMuted}>, {it.issuer}</Text>
                ) : null}
              </Text>
              <Text style={S.itemDate}>
                {it.issueDate ? fmtMonth(it.issueDate) : ""}
                {it.expirationDate ? ` — expires ${fmtMonth(it.expirationDate)}` : ""}
              </Text>
            </View>
            {(it.credentialId || it.credentialUrl) && (
              <Text style={S.meta}>
                {it.credentialId ? `ID: ${it.credentialId}` : ""}
                {it.credentialId && it.credentialUrl ? "  ·  " : ""}
                {it.credentialUrl ? (
                  <Link src={it.credentialUrl} style={S.link}>
                    Verify
                  </Link>
                ) : null}
              </Text>
            )}
          </View>
        ))}
      </View>
    );
  }

  function LanguagesBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Languages</SectionHeading>
        <Text style={S.body}>
          {items.map((it, i) => (
            <Text key={it.id}>
              {i > 0 ? <Text style={S.sep}>  |  </Text> : null}
              <Text style={S.labelStrong}>{it.language}</Text>
              {it.proficiency ? ` (${it.proficiency})` : ""}
            </Text>
          ))}
        </Text>
      </View>
    );
  }

  function AwardsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Awards</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>{it.title}</Text>
              <Text style={S.itemDate}>{fmtMonth(it.date)}</Text>
            </View>
            {it.awarder ? <Text style={S.itemSubtitle}>{it.awarder}</Text> : null}
            {it.summary ? <Text style={S.body}>{it.summary}</Text> : null}
          </View>
        ))}
      </View>
    );
  }

  function AchievementsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Achievements</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>{it.title}</Text>
              <Text style={S.itemDate}>{fmtMonth(it.date)}</Text>
            </View>
            {it.description ? <Text style={S.body}>{it.description}</Text> : null}
          </View>
        ))}
      </View>
    );
  }

  function PublicationsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Publications</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>{it.title}</Text>
              <Text style={S.itemDate}>{fmtMonth(it.date)}</Text>
            </View>
            <Text style={S.meta}>
              {joinNonEmpty([it.authors, it.publisher])}
              {it.url ? (
                <Text>
                  {" · "}
                  <Link src={it.url} style={S.link}>
                    Link
                  </Link>
                </Text>
              ) : null}
            </Text>
            {it.description ? <Text style={S.body}>{it.description}</Text> : null}
          </View>
        ))}
      </View>
    );
  }

  function VolunteerBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Volunteer Experience</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>
                {it.role}
                {it.role && it.organization ? ", " : ""}
                <Text style={S.itemTitleAlt}>{it.organization}</Text>
              </Text>
              <Text style={S.itemDate}>
                {dateRange(it.startDate, it.endDate, it.current)}
              </Text>
            </View>
            {it.location ? <Text style={S.itemSubtitle}>{it.location}</Text> : null}
            <Bullets items={it.highlights} />
          </View>
        ))}
      </View>
    );
  }

  function CourseworkBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Coursework</SectionHeading>
        <Text style={S.body}>{items.join(", ")}</Text>
      </View>
    );
  }

  function InterestsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Interests</SectionHeading>
        <Text style={S.body}>{items.join(" · ")}</Text>
      </View>
    );
  }

  function ReferencesBlock({ data }) {
    if (!data) return null;
    if (data.mode === REFERENCE_MODE.AVAILABLE_ON_REQUEST) {
      return (
        <View style={S.section}>
          <SectionHeading>References</SectionHeading>
          <Text style={[S.body, S.italic]}>Available on request.</Text>
        </View>
      );
    }
    const items = data.items ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>References</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={[S.itemGroup, S.itemGroupTight]} wrap={false}>
            <Text style={S.itemTitle}>{it.name}</Text>
            <Text style={S.meta}>
              {joinNonEmpty([it.title, it.organization], ", ")}
            </Text>
            <Text style={S.meta}>{joinNonEmpty([it.email, it.phone])}</Text>
            {it.relationship ? (
              <Text style={[S.meta, S.italic]}>{it.relationship}</Text>
            ) : null}
          </View>
        ))}
      </View>
    );
  }

  function MembershipsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Professional Memberships</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={[S.itemGroup, S.itemGroupTight]} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>
                {it.organization}
                {it.role ? (
                  <Text style={S.itemTitleMuted}> — {it.role}</Text>
                ) : null}
              </Text>
              <Text style={S.itemDate}>
                {dateRange(it.startDate, it.endDate, it.current)}
              </Text>
            </View>
            {it.membershipId ? (
              <Text style={S.meta}>Member ID: {it.membershipId}</Text>
            ) : null}
          </View>
        ))}
      </View>
    );
  }

  function SpeakingBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Speaking & Conferences</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>{it.title}</Text>
              <Text style={S.itemDate}>{fmtMonth(it.date)}</Text>
            </View>
            <Text style={S.itemSubtitle}>{joinNonEmpty([it.event, it.location])}</Text>
            {it.description ? <Text style={S.body}>{it.description}</Text> : null}
            {it.url ? (
              <Text style={S.meta}>
                <Link src={it.url} style={S.link}>
                  {it.url}
                </Link>
              </Text>
            ) : null}
          </View>
        ))}
      </View>
    );
  }

  function SpacerBlock({ data }) {
    const size = Number.isFinite(data?.size) ? data.size : 0;
    if (size <= 0) return null;
    // Renders as raw vertical whitespace in the PDF.
    return <View style={{ height: size }} />;
  }

  function PatentsBlock({ data }) {
    const items = data ?? [];
    if (!items.length) return null;
    return (
      <View style={S.section}>
        <SectionHeading>Patents</SectionHeading>
        {items.map((it) => (
          <View key={it.id} style={S.itemGroup} wrap={false}>
            <View style={S.itemRow}>
              <Text style={S.itemTitle}>{it.title}</Text>
              <Text style={S.itemDate}>
                {it.status ? `${it.status} · ` : ""}
                {fmtMonth(it.date)}
              </Text>
            </View>
            <Text style={S.meta}>
              {it.patentNumber ? `No. ${it.patentNumber}` : ""}
              {it.patentNumber && it.inventors ? "  ·  " : ""}
              {it.inventors ? `Inventors: ${it.inventors}` : ""}
            </Text>
            {it.description ? <Text style={S.body}>{it.description}</Text> : null}
          </View>
        ))}
      </View>
    );
  }

  return {
    [RESUME_SECTION_TYPES.PERSONAL_INFO]: PersonalInfoBlock,
    [RESUME_SECTION_TYPES.SUMMARY]: SummaryBlock,
    [RESUME_SECTION_TYPES.EXPERIENCE]: ExperienceBlock,
    [RESUME_SECTION_TYPES.EDUCATION]: EducationBlock,
    [RESUME_SECTION_TYPES.SKILLS]: SkillsBlock,
    [RESUME_SECTION_TYPES.PROJECTS]: ProjectsBlock,
    [RESUME_SECTION_TYPES.CERTIFICATIONS]: CertificationsBlock,
    [RESUME_SECTION_TYPES.LANGUAGES]: LanguagesBlock,
    [RESUME_SECTION_TYPES.AWARDS]: AwardsBlock,
    [RESUME_SECTION_TYPES.ACHIEVEMENTS]: AchievementsBlock,
    [RESUME_SECTION_TYPES.PUBLICATIONS]: PublicationsBlock,
    [RESUME_SECTION_TYPES.VOLUNTEER]: VolunteerBlock,
    [RESUME_SECTION_TYPES.COURSEWORK]: CourseworkBlock,
    [RESUME_SECTION_TYPES.INTERESTS]: InterestsBlock,
    [RESUME_SECTION_TYPES.REFERENCES]: ReferencesBlock,
    [RESUME_SECTION_TYPES.MEMBERSHIPS]: MembershipsBlock,
    [RESUME_SECTION_TYPES.SPEAKING]: SpeakingBlock,
    [RESUME_SECTION_TYPES.PATENTS]: PatentsBlock,
    [RESUME_SECTION_TYPES.SPACER]: SpacerBlock,
  };
}
